import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Building2, Mail, Lock, User, Briefcase, AlertCircle } from 'lucide-react';

const RegisterPage = () => {
  const { register, googleLogin, isAuthenticated } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('passwordTooShort'));
      return;
    }

    setLoading(true);

    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.companyName || null
    );
    
    if (result.success) {
      if (result.pending) {
        setError('');
        setPendingMessage(language === 'tr' ? 'Hesabınız oluşturuldu! Yönetici onayı bekleniyor. Onaylandıktan sonra giriş yapabilirsiniz.' : 'Account created! Waiting for admin approval. You can login after approval.');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A0A0A] text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <Building2 className="w-10 h-10" />
            <span className="font-heading text-2xl font-bold tracking-tight">RentFlow</span>
          </div>
          <h1 className="font-heading text-5xl font-black tracking-tighter leading-none mb-6">
            {language === 'tr' ? 'Başlayın.' : 'Get Started.'}
            <br />
            {language === 'tr' ? 'Büyüyün.' : 'Scale Up.'}
          </h1>
          <p className="text-gray-400 text-lg max-w-md">
            {language === 'tr' 
              ? 'Şirketinizi kaydedin ve sınırsız mülk, bina ve birim yönetin.'
              : 'Register your company and manage unlimited properties, buildings, and units.'}
          </p>
        </div>
        <div className="text-sm text-gray-500">
          © 2026 RentFlow. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-8">
            <div className="flex border border-black">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-sm font-medium transition-colors ${
                  language === 'en' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                }`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('tr')}
                className={`px-3 py-1 text-sm font-medium transition-colors ${
                  language === 'tr' ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'
                }`}
                data-testid="lang-tr-btn"
              >
                TR
              </button>
            </div>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Building2 className="w-8 h-8" />
            <span className="font-heading text-xl font-bold tracking-tight">RentFlow</span>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">
            {t('createAccount')}
          </h2>
          <p className="text-gray-500 mb-8">
            {language === 'tr' ? 'Şirketinizi kaydedin' : 'Register your company'}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {pendingMessage && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 flex items-center gap-3 text-emerald-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{pendingMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('fullName')} *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={language === 'tr' ? 'Adınız Soyadınız' : 'Your full name'}
                  className="pl-10 rounded-none border-gray-300 focus:border-black focus:ring-black"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('companyName')} ({t('optional')})
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={language === 'tr' ? 'Şirket adı' : 'Company name'}
                  className="pl-10 rounded-none border-gray-300 focus:border-black focus:ring-black"
                  data-testid="register-company-input"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {language === 'tr' 
                  ? 'Şirket adı girerseniz şirket yöneticisi olarak kaydolursunuz'
                  : 'Enter company name to register as company admin'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')} *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@company.com"
                  className="pl-10 rounded-none border-gray-300 focus:border-black focus:ring-black"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('password')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 rounded-none border-gray-300 focus:border-black focus:ring-black"
                  required
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('confirmPassword')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="pl-10 rounded-none border-gray-300 focus:border-black focus:ring-black"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white rounded-none py-3 hover:bg-gray-800 transition-colors"
              data-testid="register-submit-btn"
            >
              {loading ? t('loading') : t('createAccount')}
            </Button>
          </form>

          <div className="my-8 flex items-center">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-sm text-gray-500">{t('orContinueWith')}</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          <Button
            type="button"
            onClick={googleLogin}
            variant="outline"
            className="w-full rounded-none border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('signInWithGoogle')}
          </Button>

          <p className="mt-8 text-center text-sm text-gray-600">
            {language === 'tr' ? 'Zaten hesabınız var mı?' : 'Already have an account?'}{' '}
            <Link to="/login" className="font-medium text-black hover:underline" data-testid="login-link">
              {t('login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
